/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// allow-any-unicode-comment-file
// DO NOT EDIT -- auto-generated by scripts/sync-agent-host-protocol.ts

import { ActionType } from './actions.js';
import { SessionLifecycle, SessionStatus, TurnState, ToolCallStatus, ToolCallConfirmationReason, ToolCallCancellationReason, ResponsePartKind, PendingMessageKind, type IRootState, type ISessionState, type IToolCallState, type IResponsePart, type IToolCallResponsePart, type ITurn, type IPendingMessage } from './state.js';
import { IS_CLIENT_DISPATCHABLE, type IRootAction, type ISessionAction, type IClientSessionAction } from './action-origin.generated.js';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Soft assertion for exhaustiveness checking. Place in the `default` branch of
 * a switch on a discriminated union so the compiler errors when a new variant
 * is added but not handled.
 *
 * At runtime, logs a warning instead of throwing so that forward-compatible
 * clients receiving unknown actions from a newer server degrade gracefully.
 */
export function softAssertNever(value: never, log?: (msg: string) => void): void {
	const msg = `Unhandled action type: ${JSON.stringify(value)}`;
	(log ?? console.warn)(msg);
}

/** Extracts the common base fields shared by all tool call lifecycle states. */
function tcBase(tc: IToolCallState) {
	return {
		toolCallId: tc.toolCallId,
		toolName: tc.toolName,
		displayName: tc.displayName,
		toolClientId: tc.toolClientId,
		_meta: tc._meta,
	};
}

/**
 * Ends the active turn, finalizing it into a completed turn record.
 *
 * Tool call parts with non-terminal states are forced to cancelled.
 * Pending permissions are stripped from tool call parts.
 */
function endTurn(
	state: ISessionState,
	turnId: string,
	turnState: TurnState,
	summaryStatus: SessionStatus,
	error?: { errorType: string; message: string; stack?: string },
): ISessionState {
	if (!state.activeTurn || state.activeTurn.id !== turnId) {
		return state;
	}
	const active = state.activeTurn;

	const responseParts: IResponsePart[] = active.responseParts.map(part => {
		if (part.kind !== ResponsePartKind.ToolCall) {
			return part;
		}
		const tc = part.toolCall;
		if (tc.status === ToolCallStatus.Completed || tc.status === ToolCallStatus.Cancelled) {
			return part;
		}
		// Force non-terminal tool calls into cancelled state
		return {
			kind: ResponsePartKind.ToolCall,
			toolCall: {
				status: ToolCallStatus.Cancelled as const,
				...tcBase(tc),
				invocationMessage: tc.status === ToolCallStatus.Streaming ? (tc.invocationMessage ?? '') : tc.invocationMessage,
				toolInput: tc.status === ToolCallStatus.Streaming ? undefined : tc.toolInput,
				reason: ToolCallCancellationReason.Skipped,
			},
		};
	});

	const turn: ITurn = {
		id: active.id,
		userMessage: active.userMessage,
		responseParts,
		usage: active.usage,
		state: turnState,
		error,
	};

	return {
		...state,
		turns: [...state.turns, turn],
		activeTurn: undefined,
		summary: { ...state.summary, status: summaryStatus, modifiedAt: Date.now() },
	};
}

/**
 * Immutably updates the tool call inside a `ToolCall` response part in the
 * active turn's `responseParts` array. Returns `state` unchanged if the
 * active turn or tool call doesn't match.
 */
function updateToolCallInParts(
	state: ISessionState,
	turnId: string,
	toolCallId: string,
	updater: (tc: IToolCallState) => IToolCallState,
): ISessionState {
	const activeTurn = state.activeTurn;
	if (!activeTurn || activeTurn.id !== turnId) {
		return state;
	}

	let found = false;
	const responseParts = activeTurn.responseParts.map(part => {
		if (part.kind === ResponsePartKind.ToolCall && part.toolCall.toolCallId === toolCallId) {
			const updated = updater(part.toolCall);
			if (updated === part.toolCall) {
				return part;
			}
			found = true;
			return { ...part, toolCall: updated };
		}
		return part;
	});

	if (!found) {
		return state;
	}

	return {
		...state,
		activeTurn: { ...activeTurn, responseParts },
	};
}

/**
 * Immutably updates a response part by `partId` in the active turn.
 * For markdown/reasoning parts, matches on `id`. For tool call parts,
 * matches on `toolCall.toolCallId`.
 */
function updateResponsePart(
	state: ISessionState,
	turnId: string,
	partId: string,
	updater: (part: IResponsePart) => IResponsePart,
): ISessionState {
	const activeTurn = state.activeTurn;
	if (!activeTurn || activeTurn.id !== turnId) {
		return state;
	}

	let found = false;
	const responseParts = activeTurn.responseParts.map(part => {
		if (!found) {
			const id = part.kind === ResponsePartKind.ToolCall
				? part.toolCall.toolCallId
				: 'id' in part ? part.id : undefined;
			if (id === partId) {
				found = true;
				return updater(part);
			}
		}
		return part;
	});

	if (!found) {
		return state;
	}

	return {
		...state,
		activeTurn: { ...activeTurn, responseParts },
	};
}

// ─── Root Reducer ────────────────────────────────────────────────────────────

/**
 * Pure reducer for root state. Handles all {@link IRootAction} variants.
 */
export function rootReducer(state: IRootState, action: IRootAction, log?: (msg: string) => void): IRootState {
	switch (action.type) {
		case ActionType.RootAgentsChanged:
			return { ...state, agents: action.agents };

		case ActionType.RootActiveSessionsChanged:
			return { ...state, activeSessions: action.activeSessions };

		default:
			softAssertNever(action, log);
			return state;
	}
}

// ─── Session Reducer ─────────────────────────────────────────────────────────

/**
 * Pure reducer for session state. Handles all {@link ISessionAction} variants.
 */
export function sessionReducer(state: ISessionState, action: ISessionAction, log?: (msg: string) => void): ISessionState {
	switch (action.type) {
		// ── Lifecycle ──────────────────────────────────────────────────────────

		case ActionType.SessionReady:
			return {
				...state,
				lifecycle: SessionLifecycle.Ready,
				summary: { ...state.summary, status: SessionStatus.Idle },
			};

		case ActionType.SessionCreationFailed:
			return {
				...state,
				lifecycle: SessionLifecycle.CreationFailed,
				creationError: action.error,
			};

		// ── Turn Lifecycle ────────────────────────────────────────────────────

		case ActionType.SessionTurnStarted: {
			let next: ISessionState = {
				...state,
				summary: { ...state.summary, status: SessionStatus.InProgress, modifiedAt: Date.now() },
				activeTurn: {
					id: action.turnId,
					userMessage: action.userMessage,
					responseParts: [],
					usage: undefined,
				},
			};

			// If this turn was auto-started from a pending message, remove it
			if (action.queuedMessageId) {
				if (next.steeringMessage?.id === action.queuedMessageId) {
					next = { ...next, steeringMessage: undefined };
				}
				if (next.queuedMessages) {
					const filtered = next.queuedMessages.filter(m => m.id !== action.queuedMessageId);
					next = { ...next, queuedMessages: filtered.length > 0 ? filtered : undefined };
				}
			}

			return next;
		}

		case ActionType.SessionDelta:
			return updateResponsePart(state, action.turnId, action.partId, part => {
				if (part.kind === ResponsePartKind.Markdown) {
					return { ...part, content: part.content + action.content };
				}
				return part;
			});

		case ActionType.SessionResponsePart:
			if (!state.activeTurn || state.activeTurn.id !== action.turnId) {
				return state;
			}
			return {
				...state,
				activeTurn: {
					...state.activeTurn,
					responseParts: [...state.activeTurn.responseParts, action.part],
				},
			};

		case ActionType.SessionTurnComplete:
			return endTurn(state, action.turnId, TurnState.Complete, SessionStatus.Idle);

		case ActionType.SessionTurnCancelled:
			return endTurn(state, action.turnId, TurnState.Cancelled, SessionStatus.Idle);

		case ActionType.SessionError:
			return endTurn(state, action.turnId, TurnState.Error, SessionStatus.Error, action.error);

		// ── Tool Call State Machine ───────────────────────────────────────────

		case ActionType.SessionToolCallStart:
			if (!state.activeTurn || state.activeTurn.id !== action.turnId) {
				return state;
			}
			return {
				...state,
				activeTurn: {
					...state.activeTurn,
					responseParts: [
						...state.activeTurn.responseParts,
						{
							kind: ResponsePartKind.ToolCall,
							toolCall: {
								toolCallId: action.toolCallId,
								toolName: action.toolName,
								displayName: action.displayName,
								toolClientId: action.toolClientId,
								_meta: action._meta,
								status: ToolCallStatus.Streaming,
							},
						} satisfies IToolCallResponsePart,
					],
				},
			};

		case ActionType.SessionToolCallDelta:
			return updateToolCallInParts(state, action.turnId, action.toolCallId, tc => {
				if (tc.status !== ToolCallStatus.Streaming) {
					return tc;
				}
				return {
					...tc,
					partialInput: (tc.partialInput ?? '') + action.content,
					invocationMessage: action.invocationMessage ?? tc.invocationMessage,
				};
			});

		case ActionType.SessionToolCallReady:
			return updateToolCallInParts(state, action.turnId, action.toolCallId, tc => {
				if (tc.status !== ToolCallStatus.Streaming && tc.status !== ToolCallStatus.Running) {
					return tc;
				}
				const base = tcBase(tc);
				if (action.confirmed) {
					return {
						status: ToolCallStatus.Running,
						...base,
						invocationMessage: action.invocationMessage,
						toolInput: action.toolInput,
						confirmed: action.confirmed,
					};
				}
				return {
					status: ToolCallStatus.PendingConfirmation,
					...base,
					invocationMessage: action.invocationMessage,
					toolInput: action.toolInput,
					confirmationTitle: action.confirmationTitle,
				};
			});

		case ActionType.SessionToolCallConfirmed:
			return updateToolCallInParts(state, action.turnId, action.toolCallId, tc => {
				if (tc.status !== ToolCallStatus.PendingConfirmation) {
					return tc;
				}
				const base = tcBase(tc);
				if (action.approved) {
					return {
						status: ToolCallStatus.Running,
						...base,
						invocationMessage: tc.invocationMessage,
						toolInput: tc.toolInput,
						confirmed: action.confirmed,
					};
				}
				return {
					status: ToolCallStatus.Cancelled,
					...base,
					invocationMessage: tc.invocationMessage,
					toolInput: tc.toolInput,
					reason: action.reason,
					reasonMessage: action.reasonMessage,
					userSuggestion: action.userSuggestion,
				};
			});

		case ActionType.SessionToolCallComplete:
			return updateToolCallInParts(state, action.turnId, action.toolCallId, tc => {
				if (tc.status !== ToolCallStatus.Running && tc.status !== ToolCallStatus.PendingConfirmation) {
					return tc;
				}
				const base = tcBase(tc);
				const confirmed = tc.status === ToolCallStatus.Running
					? tc.confirmed
					: ToolCallConfirmationReason.NotNeeded;
				if (action.requiresResultConfirmation) {
					return {
						status: ToolCallStatus.PendingResultConfirmation,
						...base,
						invocationMessage: tc.invocationMessage,
						toolInput: tc.toolInput,
						confirmed,
						...action.result,
					};
				}
				return {
					status: ToolCallStatus.Completed,
					...base,
					invocationMessage: tc.invocationMessage,
					toolInput: tc.toolInput,
					confirmed,
					...action.result,
				};
			});

		case ActionType.SessionToolCallResultConfirmed:
			return updateToolCallInParts(state, action.turnId, action.toolCallId, tc => {
				if (tc.status !== ToolCallStatus.PendingResultConfirmation) {
					return tc;
				}
				const base = tcBase(tc);
				if (action.approved) {
					return {
						status: ToolCallStatus.Completed,
						...base,
						invocationMessage: tc.invocationMessage,
						toolInput: tc.toolInput,
						confirmed: tc.confirmed,
						success: tc.success,
						pastTenseMessage: tc.pastTenseMessage,
						content: tc.content,
						structuredContent: tc.structuredContent,
						error: tc.error,
					};
				}
				return {
					status: ToolCallStatus.Cancelled,
					...base,
					invocationMessage: tc.invocationMessage,
					toolInput: tc.toolInput,
					reason: ToolCallCancellationReason.ResultDenied,
				};
			});

		// ── Metadata ──────────────────────────────────────────────────────────

		case ActionType.SessionTitleChanged:
			return {
				...state,
				summary: { ...state.summary, title: action.title, modifiedAt: Date.now() },
			};

		case ActionType.SessionUsage:
			if (!state.activeTurn || state.activeTurn.id !== action.turnId) {
				return state;
			}
			return {
				...state,
				activeTurn: { ...state.activeTurn, usage: action.usage },
			};

		case ActionType.SessionReasoning:
			return updateResponsePart(state, action.turnId, action.partId, part => {
				if (part.kind === ResponsePartKind.Reasoning) {
					return { ...part, content: part.content + action.content };
				}
				return part;
			});

		case ActionType.SessionModelChanged:
			return {
				...state,
				summary: { ...state.summary, model: action.model, modifiedAt: Date.now() },
			};

		case ActionType.SessionServerToolsChanged:
			return { ...state, serverTools: action.tools };

		case ActionType.SessionActiveClientChanged:
			return {
				...state,
				activeClient: action.activeClient ?? undefined,
			};

		case ActionType.SessionActiveClientToolsChanged:
			if (!state.activeClient) {
				return state;
			}
			return {
				...state,
				activeClient: { ...state.activeClient, tools: action.tools },
			};

		// ── Customizations ──────────────────────────────────────────────────

		case ActionType.SessionCustomizationsChanged:
			return { ...state, customizations: action.customizations };

		case ActionType.SessionCustomizationToggled: {
			const list = state.customizations;
			if (!list) {
				return state;
			}
			const idx = list.findIndex(c => c.customization.uri === action.uri);
			if (idx < 0) {
				return state;
			}
			const updated = [...list];
			updated[idx] = { ...list[idx], enabled: action.enabled };
			return { ...state, customizations: updated };
		}

		// ── Truncation ────────────────────────────────────────────────────────

		case ActionType.SessionTruncated: {
			let turns: typeof state.turns;
			if (action.turnId === undefined) {
				turns = [];
			} else {
				const idx = state.turns.findIndex(t => t.id === action.turnId);
				if (idx < 0) {
					return state;
				}
				turns = state.turns.slice(0, idx + 1);
			}
			return {
				...state,
				turns,
				activeTurn: undefined,
				summary: { ...state.summary, status: SessionStatus.Idle, modifiedAt: Date.now() },
			};
		}

		// ── Pending Messages ──────────────────────────────────────────────────

		case ActionType.SessionPendingMessageSet: {
			const entry: IPendingMessage = { id: action.id, userMessage: action.userMessage };
			if (action.kind === PendingMessageKind.Steering) {
				return { ...state, steeringMessage: entry };
			}
			const existing = state.queuedMessages ?? [];
			const idx = existing.findIndex(m => m.id === action.id);
			if (idx >= 0) {
				const updated = [...existing];
				updated[idx] = entry;
				return { ...state, queuedMessages: updated };
			}
			return { ...state, queuedMessages: [...existing, entry] };
		}

		case ActionType.SessionPendingMessageRemoved: {
			if (action.kind === PendingMessageKind.Steering) {
				if (!state.steeringMessage || state.steeringMessage.id !== action.id) {
					return state;
				}
				return { ...state, steeringMessage: undefined };
			}
			const existing = state.queuedMessages;
			if (!existing) {
				return state;
			}
			const filtered = existing.filter(m => m.id !== action.id);
			return filtered.length === existing.length
				? state
				: { ...state, queuedMessages: filtered.length > 0 ? filtered : undefined };
		}

		case ActionType.SessionQueuedMessagesReordered: {
			const existing = state.queuedMessages;
			if (!existing) {
				return state;
			}
			const byId = new Map(existing.map(m => [m.id, m]));
			const ordered = new Set<string>();
			const reordered = action.order
				.filter(id => {
					if (byId.has(id) && !ordered.has(id)) {
						ordered.add(id);
						return true;
					}
					return false;
				})
				.map(id => byId.get(id)!);
			// Append any messages not mentioned in order, preserving original order
			for (const m of existing) {
				if (!ordered.has(m.id)) {
					reordered.push(m);
				}
			}
			return { ...state, queuedMessages: reordered };
		}

		default:
			softAssertNever(action, log);
			return state;
	}
}

// ─── Dispatch Validation ─────────────────────────────────────────────────────

/**
 * Type guard that checks whether an action may be dispatched by a client.
 *
 * Servers SHOULD call this to validate incoming `dispatchAction` requests
 * and reject any action the client is not allowed to originate.
 */
export function isClientDispatchable(action: ISessionAction): action is IClientSessionAction {
	return IS_CLIENT_DISPATCHABLE[action.type];
}
